import { Raffle } from "@/app/entities/Raffle";
import { PaginationResponse } from "@/app/types";
import { RaffleFormValues } from "@/view/pages/Sorteios/hooks/useRaffleForm";
import { httpClient } from "../http/httpClient";

const API_URL = "/prizeDraw";

const parseFormValuesToFormData = (payload: RaffleFormValues) => {
  const formData = new FormData();
  formData.append("name", payload.name);
  formData.append("description", payload.description);
  formData.append("endDate", payload.endDate.toISOString());
  formData.append("couponValue", payload.couponValue.toString());
  formData.append("startDate", payload.startDate.toISOString());
  formData.append("resultDate", payload.resultDate.toISOString());

  if (payload.couponCode) {
    formData.append("couponCode", payload.couponCode);
  }

  formData.append(
    "identificadorSorteioStart",
    (payload.identificadorSorteioStart ?? "").toString(),
  );

  payload.gallery?.forEach((image) => {
    formData.append("gallery", image);
  });

  if (payload.banner) {
    formData.append("banner", payload.banner);
  }

  payload.prizes.forEach((prize) => {
    formData.append("prizeName[]", prize.name);
  });

  return formData;
};

interface RafflesPaginate extends PaginationResponse {
  prizeDraws: Raffle[];
}

export interface CouponItem {
  _id: string;
  code: string;
  prizeDraw: string;
  user: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface CouponsResponse {
  coupons: CouponItem[];
  exports: boolean;
  pagination: {
    skip: number;
    limit: number;
    total: number;
    sort: string;
  };
}

interface PrizeDrawHashResponse {
  hash: string;
  coupons: {
    id: string;
    code: string;
  }[];
  prizeDraw: {
    id: string;
    name: string;
    description: string;
    banner: string;
  };
}

export const raffleService = {
  async draw(raffleId: string, code?: string): Promise<void> {
    await httpClient.get(`${API_URL}/draw/${raffleId}`, {
      params: { code },
    });
  },

  async findAll(
    search: string,
    skip: number,
    limit: number,
    endDate?: Date,
    isActive?: boolean,
  ): Promise<RafflesPaginate> {
    const { data } = await httpClient.get<RafflesPaginate>(API_URL, {
      params: { search, skip, limit, endDate, isActive },
    });
    return data;
  },

  async findById(raffleId: string): Promise<Raffle | undefined> {
    const response = await httpClient.get<Raffle>(`${API_URL}/${raffleId}`);
    return response.data;
  },

  async getCoupons(raffleId: string): Promise<CouponsResponse> {
    const response = await httpClient.get<CouponsResponse>(
      `${API_URL}/${raffleId}/coupons`,
    );
    return response.data;
  },

  async create(payload: RaffleFormValues): Promise<Raffle> {
    const formData = parseFormValuesToFormData(payload);
    const response = await httpClient.post<Raffle>(API_URL, formData);
    return response?.data;
  },

  async update(raffleId: string, payload: RaffleFormValues): Promise<Raffle> {
    const formData = parseFormValuesToFormData(payload);
    const response = await httpClient.put<Raffle>(
      `${API_URL}/${raffleId}`,
      formData,
    );
    return response?.data;
  },

  async delete(raffleId: string): Promise<void> {
    await httpClient.delete(`${API_URL}/${raffleId}`);
  },

  async deletePrize(raffleId: string, prizeIndex: number): Promise<void> {
    await httpClient.delete(`${API_URL}/prize/${raffleId}/${prizeIndex}`);
  },

  async getPrizeDrawHash(raffleId: string, userId: string) {
    const response = await httpClient
      .get<PrizeDrawHashResponse>(`${API_URL}/${raffleId}/hash/${userId}`)
      .catch((err) => {
        throw err?.response?.data || err;
      });
    return response.data;
  },
};
